import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { safeLog } from '@/utils/safeLogger';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Navbar } from '@/components/Navbar';
import { Loader2, AlertCircle, CheckCircle2, RefreshCw, ShieldCheck } from 'lucide-react';
import logo from '@/assets/logo.png';

const MicrosoftLogo = ({ className = "" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 21 21" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

export default function Auth() {
  const [isLoading, setIsLoading] = useState(false);
  const [error] = useState<string>('');
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [autoLoginInitiated, setAutoLoginInitiated] = useState(false);
  const [showFallbackButton, setShowFallbackButton] = useState(false);
  const [showTimeoutHint, setShowTimeoutHint] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const redirectTo = searchParams.get('redirect') || '/settings';
  const isDialogMode = searchParams.get('mode') === 'dialog';
  const [qrAuthSuccess] = useState(false);

  useEffect(() => {
    const checkAuthAndRedirect = async () => {
      if (isDialogMode) {
        safeLog.info('📱 Dialog mode: Ignoring existing session, showing login form');
        return;
      }
      if (user) {
        safeLog.info('[Auth] Session detected, redirecting to:', { redirectTo });
        setIsRedirecting(true);
        navigate(redirectTo, { replace: true });
      }
    };
    checkAuthAndRedirect();
  }, [user, navigate, redirectTo, isDialogMode]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        safeLog.info(`[Auth] onAuthStateChange event: ${event} session: ${!!session} isDialogMode: ${isDialogMode}`);
        if (event === 'SIGNED_IN' && session && !isDialogMode) {
          const redirectPath = sessionStorage.getItem('auth_redirect') || redirectTo;
          sessionStorage.removeItem('auth_redirect');
          setIsRedirecting(true);
          navigate(redirectPath, { replace: true });
        }
      }
    );
    return () => subscription.unsubscribe();
  }, [navigate, redirectTo, isDialogMode]);

  useEffect(() => {
    if (isDialogMode && !autoLoginInitiated && !user) {
      setAutoLoginInitiated(true);
      const timer = setTimeout(() => handleMicrosoftSignIn(), 100);
      return () => clearTimeout(timer);
    }
  }, [isDialogMode, autoLoginInitiated, user]);

  useEffect(() => {
    if (isDialogMode && autoLoginInitiated) {
      const timer = setTimeout(() => setShowFallbackButton(true), 5000);
      return () => clearTimeout(timer);
    }
  }, [isDialogMode, autoLoginInitiated]);

  useEffect(() => {
    if (isRedirecting && !showTimeoutHint) {
      const timer = setTimeout(() => setShowTimeoutHint(true), 8000);
      return () => clearTimeout(timer);
    }
  }, [isRedirecting, showTimeoutHint]);

  const handleMicrosoftSignIn = () => {
    const redirectAfterAuth = redirectTo || '/settings';
    sessionStorage.setItem('auth_redirect', redirectAfterAuth);

    if (isDialogMode) {
      window.location.assign('/auth-start-microsoft');
      return;
    }

    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    const popup = window.open(
      '/auth-start-microsoft',
      'Microsoft Login',
      `width=${width},height=${height},left=${left},top=${top},popup=1`
    );

    if (!popup) {
      safeLog.warn('[Auth] Popup blocked, falling back to redirect');
      window.location.assign('/auth-start-microsoft');
      return;
    }

    const handleMessage = async (event: MessageEvent) => {
      if (event.data.type === 'microsoft-auth-success') {
        popup?.close();
        window.removeEventListener('message', handleMessage);

        if (event.data.session) {
          await supabase.auth.setSession({
            access_token: event.data.session.access_token,
            refresh_token: event.data.session.refresh_token
          });

          if (isDialogMode && (window as any).Office?.context?.ui?.messageParent) {
            (window as any).Office.context.ui.messageParent(JSON.stringify({
              type: 'auth-success',
              session: event.data.session,
              graphToken: event.data.graphToken
            }));
          } else {
            setTimeout(() => navigate(redirectTo, { replace: true }), 1000);
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
  };

  if (qrAuthSuccess) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 app-surface bg-dot-grid">
        <div className="app-tile w-full max-w-md p-10 text-center">
          <div className="mx-auto mb-5 w-14 h-14 rounded-full bg-emerald-500/10 ring-1 ring-emerald-500/20 flex items-center justify-center">
            <CheckCircle2 className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="font-display text-2xl font-semibold mb-2 tracking-tight">Authentication successful</h2>
          <p className="text-muted-foreground">
            You can now close this window and return to your Outlook Add-In.
          </p>
        </div>
      </div>
    );
  }

  if (isRedirecting) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3 app-surface bg-dot-grid">
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Redirecting…</p>
        {showTimeoutHint && (
          <div className="mt-4 app-tile p-5 max-w-md text-center">
            <p className="text-sm text-muted-foreground mb-3">
              Taking longer than expected? This might be a connection issue.
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setIsRedirecting(false);
                setShowTimeoutHint(false);
              }}
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Try again
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (isDialogMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4 p-8 max-w-sm">
          <Loader2 className="h-9 w-9 animate-spin mx-auto text-primary" />
          <p className="text-sm text-muted-foreground">Redirecting to Microsoft login…</p>

          {showFallbackButton && (
            <div className="space-y-3 pt-4">
              <div className="rounded-lg border border-border bg-muted/40 p-3">
                <p className="text-xs text-muted-foreground">
                  If you're not redirected automatically, click below to continue.
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="lg"
                className="w-full bg-white hover:bg-white/95 text-[#1f1f1f] border-[#dadce0] dark:bg-white dark:hover:bg-white/95 dark:text-[#1f1f1f]"
                onClick={handleMicrosoftSignIn}
              >
                <MicrosoftLogo className="mr-2 h-4 w-4" />
                Sign in with Microsoft
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col app-surface">
      <Navbar />
      <main className="flex-1 py-16 bg-dot-grid flex items-center">
        <div className="container px-4 max-w-md mx-auto">
          <div className="app-tile p-8 md:p-10 shadow-fluent-32 animate-fade-in-up">
            <div className="text-center mb-8">
              <img
                src={logo}
                alt="Email2Contact"
                className="w-12 h-12 mx-auto mb-4"
              />
              <h1 className="font-display text-2xl font-semibold tracking-tight mb-2">
                Welcome to Email2Contact
              </h1>
              <p className="text-sm text-muted-foreground">
                Sign in with your Microsoft account to access your settings
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="mb-5">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="button"
              size="lg"
              className="w-full bg-white hover:bg-white/95 text-[#1f1f1f] border border-[#dadce0] shadow-fluent-2 dark:bg-white dark:hover:bg-white/95 dark:text-[#1f1f1f]"
              onClick={handleMicrosoftSignIn}
              disabled={isLoading}
            >
              <MicrosoftLogo className="mr-2.5 h-4 w-4" />
              {isLoading ? 'Signing in…' : 'Sign in with Microsoft'}
            </Button>

            <div className="mt-6 pt-6 border-t border-border/60 flex items-start gap-2 text-xs text-muted-foreground">
              <ShieldCheck className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-emerald-600 dark:text-emerald-400" />
              <p>
                Secure single sign-on. Your credentials stay with Microsoft —
                we only receive a verified session token.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
